import { Clipboard } from '@angular/cdk/clipboard';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, DestroyRef, HostBinding, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { map, shareReplay } from 'rxjs/operators';

// AG-Grid Imports
import {
  CellClickedEvent,
  CsvExportParams,
  GridApi,
  GridOptions,
  SelectionChangedEvent
} from 'ag-grid-community';

// PrimeNG Imports
import { CommonModule } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DrawerModule } from 'primeng/drawer';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RippleModule } from 'primeng/ripple';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';

// Components and Services
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgGridAngular } from 'ag-grid-angular';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { environment } from '../environments/environment.prod';
import { MessageDialogComponent, MessageData } from './message-dialog/message-dialog.component';
import { NoRowsOverlayComponent } from './no-rows-overlay/no-rows-overlay.component';
import { SelectBarComponent } from './select-bar/select-bar.component';
import { GridDatasourceService } from './services/gridDatasource/grid-datasource.service';
import { ProjectAndTypesService } from './services/projectandTypes/project-and-types.service';
import { SelectsService } from './services/selectService/selects.service';

// Para AG-Grid se agregará después cuando esté disponible
ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DrawerModule,
    CardModule,
    ToolbarModule,
    ButtonModule,
    ToggleButtonModule,
    TooltipModule,
    BadgeModule,
    DividerModule,
    RippleModule,
    ProgressSpinnerModule,
    SelectBarComponent,
    AgGridAngular,
    MessageDialogComponent
  ]
})
export class AppComponent implements OnInit {

  // Injected services using Angular 20 inject()
  public readonly gridDataSource = inject(GridDatasourceService);
  private readonly selectsService = inject(SelectsService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly clipboard = inject(Clipboard);
  private readonly overlay = inject(OverlayContainer);
  public readonly projectsService = inject(ProjectAndTypesService);
  private readonly destroy$ = inject(DestroyRef)

  // Signals for reactive state management
  public readonly enableCSVButton = signal<boolean>(false);
  public readonly isSidebarOpen = signal<boolean>(true);
  public readonly gridTheme = signal<string>('ag-theme-alpine');
  public readonly pageTheme = signal<string>('light');
  public readonly isHandset = signal<boolean>(false);
  public readonly messageDialogVisible = signal<boolean>(false);
  public readonly currentMessageData = signal<MessageData | null>(null);

  // Grid API reference
  private gridApi = signal<GridApi | null>(null);
  public gridOptions: GridOptions = {};

  private gridDatasourceService = inject(GridDatasourceService);
  public lastRefresh: string = '-';

  // Flag to skip first effect execution to prevent duplicate API requests
  private isFirstEffectRun = true;

  // Form controls
  public readonly toggleControl = new FormControl<boolean>(false);

  // Host binding for dark mode class (adds/removes only this class, doesn't overwrite others)
  @HostBinding('class.darkMode')
  get hostDarkModeClass(): boolean {
    return this.pageTheme() === 'dark';
  }

  // Computed properties
  public readonly drawerMode = computed(() =>
    this.isHandset() ? 'overlay' : 'push'
  );

  public readonly showMobileToggle = computed(() => this.isHandset());

  constructor() {
    // Initialize signals with stored theme
    const storedTheme = localStorage.getItem('theme') || 'light';
    this.pageTheme.set(storedTheme);
    this.gridTheme.set(storedTheme === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine');

    // Initialize breakpoint observer
    this.initializeBreakpointObserver();

    // Initialize theme control
    this.toggleControl.setValue(storedTheme === 'dark');

    // Ensure overlay container has the proper class at startup
    const darkClassName = 'darkMode';
    if (storedTheme === 'dark') {
      this.overlay.getContainerElement().classList.add(darkClassName);
    } else {
      this.overlay.getContainerElement().classList.remove(darkClassName);
    }

    // Initialize grid options
    this.initializeGridOptions();

    // Effect: listen to selectsService.fieldsValue() signal and update grid context
    // whenever the selects change. This keeps the datasource queries in sync.
    effect(() => {
      this.selectsService.fieldsValue();

      // Skip first execution to prevent duplicate API request on initialization
      if (this.isFirstEffectRun) {
        this.isFirstEffectRun = false;
        return;
      }

      this.gridDataSource.resetDrawAndCursor();

      // if the grid is ready, purge its cache so datasource will be requested again
      if (this.gridApi() && this.projectsService.isDataLoaded()) {
        // purgeInfiniteCache exists on the grid API for infinite row model
        this.gridApi()?.purgeInfiniteCache?.();
      }
    });
  }

  ngOnInit(): void {
    this.setupThemeControl();
  }

  /**
   * Initialize breakpoint observer for responsive design
   */
  private initializeBreakpointObserver(): void {
    this.breakpointObserver.observe(Breakpoints.Handset)
      .pipe(
        map(result => result.matches),
        shareReplay(),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe(isHandset => {
        this.isHandset.set(isHandset);
      });
  }

  /**
   * Initialize AG-Grid options
   */
  private initializeGridOptions(): void {
    this.gridOptions = {
      columnDefs: [
        {
          headerName: 'Date',
          field: 'DateTime',
        },
        {
          headerName: 'Type',
          field: 'Type',
        },
        {
          headerName: 'Message',
          field: 'Subject',
          onCellClicked: (event: CellClickedEvent) => this.showMessage(event),
        },
        {
          headerName: 'Project',
          field: 'Project',
        },
        {
          headerName: 'Service',
          field: 'Service',
        },
        {
          headerName: 'Cif ID',
          field: 'CifId',
        },
        {
          headerName: 'MailFilePdfName',
          field: 'MailFilePdfName',
        },
        {
          headerName: 'Box ID',
          field: 'BoxId',
          onCellClicked: (event: CellClickedEvent) => this.copyToClipboard(event)
        },
        {
          headerName: 'Folder ID',
          field: 'FolderId',
          onCellClicked: (event: CellClickedEvent) => this.copyToClipboard(event)
        }
      ],
      defaultColDef: {
        editable: false,
        sortable: false,
        resizable: true,
        suppressSizeToFit: false
      },
      components: {
        'noRowsOverlay': NoRowsOverlayComponent
      },
      rowModelType: 'infinite',
      rowSelection: { mode: 'multiRow', checkboxes: true, headerCheckbox: false },
      animateRows: true,
      cacheBlockSize: environment.paginationSize,
      paginationPageSize: environment.paginationSize,
      maxBlocksInCache: 10,
      cacheOverflowSize: 2,
      maxConcurrentDatasourceRequests: 2,
      infiniteInitialRowCount: 1,
      loadingOverlayComponent: 'noRowsOverlay',
      onGridSizeChanged: () => this.fitGrid(),
      context: {
        loadInitialData: true
      }
    }
  }

  /**
   * Setup theme control subscription
   */
  private setupThemeControl(): void {
    this.toggleControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe((darkMode: boolean | null) => {
        const isDarkMode = darkMode ?? false;
        const darkClassName = 'darkMode';

        if (isDarkMode) {
          this.overlay.getContainerElement().classList.add(darkClassName);
          this.gridTheme.set('ag-theme-alpine-dark');
          this.pageTheme.set('dark');
        } else {
          this.overlay.getContainerElement().classList.remove(darkClassName);
          this.gridTheme.set('ag-theme-alpine');
          this.pageTheme.set('light');
        }

        localStorage.setItem('theme', this.pageTheme());
      });
  }

  public onSelectionChanged(event: SelectionChangedEvent): void {
    const selectedRows = event.api.getSelectedRows();
    this.enableCSVButton.set(selectedRows.length > 0);
  }

  public deselectAll(): void {
    this.gridApi()?.deselectAll();
  }

  public toggleSidebar(): void {
    this.isSidebarOpen.set(!this.isSidebarOpen());
  }

private processCell(params: any): any {
  if (params.column.getColId() === 'Message' || params.column.getColId() === 'Subject') {
    const data = params.node?.data;
    if (!data) return params.value;
    return typeof data.Message === 'object' ? data.Message?.value : data.Message;
  }
  return params.value;
}

  public exportToCSV(): void {
    const exportParams: CsvExportParams = {
      onlySelected: true,
      fileName: `${new Date().toDateString()}.csv`,
      processCellCallback: this.processCell.bind(this)
    };

    this.gridApi()?.exportDataAsCsv(exportParams);
  }

  public exportAllToCSV(): void {
    const exportParams: CsvExportParams = {
      allColumns: true,
      fileName: `${new Date().toDateString()}.csv`,
      processCellCallback: this.processCell.bind(this)
    };

    this.gridApi()?.exportDataAsCsv(exportParams);
  }

  public copyToClipboard(event: CellClickedEvent): void {
    if (!event.value) return;

    let value: string;

    if (event.column.getColId() === 'Subject') {
      value = event.data.Message.value;
    } else {
      value = event.value;
    }

    this.clipboard.copy(value);
  }

  public showMessage(event: CellClickedEvent): void {
    const { Subject, Message, Type, DateTime, Project, Service } = event.data;

    // Set the message data for the dialog
    this.currentMessageData.set({
      subject: Subject,
      message: Message.value,
      type: Type,
      dateTime: DateTime,
      project: Project,
      service: Service
    });

    // Show the dialog
    this.messageDialogVisible.set(true);
  }

  public onMessageDialogHide(): void {
    this.messageDialogVisible.set(false);
    this.currentMessageData.set(null);
  }

  public onMessageCopied(value: string): void {
    // Optional: You could show a toast notification here
    console.log('Copied to clipboard:', value);
  }

  public onGridReady(params: any): void {
    const isDarkModeEnabled = this.pageTheme() !== 'light';

    this.toggleControl.setValue(isDarkModeEnabled);
    params.api.setGridOption('datasource', this.gridDataSource);
    this.gridApi.set(params.api);
    this.updateLastRefresh();
  }

  public fitGrid(): void {
    if (this.gridApi()) {
      this.gridApi()?.sizeColumnsToFit();
    }
  }

  refreshGrid(): void {
    const api = this.gridApi();

    if (api) {
      this.gridDatasourceService.resetDrawAndCursor();

      if (typeof api.refreshInfiniteCache === 'function') {
        api.refreshInfiniteCache();
      } else if (typeof (api as any).purgeInfiniteCache === 'function') {
        (api as any).purgeInfiniteCache();
      }

      this.updateLastRefresh();
    }
  }

  private updateLastRefresh(): void {
    const now = new Date();
    this.lastRefresh = now.toLocaleTimeString('es-ES', {
      hour12: false
    });
  }
}
